/**
 * 用户头像下拉菜单组件
 * 登录后显示在顶部导航栏右上角
 */

import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useLogoutMutation } from '@/hooks/useAuthQuery'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  Blocks,
  LayoutDashboard,
  Palette,
  Shield,
  LogOut,
} from 'lucide-react'
import { resolveMediaUrl } from '@/lib/media'

interface MenuItem {
  label: string
  icon: React.ComponentType<{ className?: string }>
  path?: string
  adminOnly?: boolean
  danger?: boolean
  onClick?: () => void
}

export function UserMenu() {
  const navigate = useNavigate()
  const { user, clearAuth, isAdmin: checkIsAdmin } = useAuthStore()
  const logoutMutation = useLogoutMutation()
  const isAdmin = checkIsAdmin()

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync()
    } catch {
      // 无论后端状态如何，前端都应清空本地会话
    } finally {
      clearAuth()
      navigate('/')
    }
  }

  const menuItems: MenuItem[] = [
    { label: '仪表板', icon: LayoutDashboard, path: '/user' },
    { label: '我的皮肤', icon: Palette, path: '/user/skins' },
    { label: '我的蓝图', icon: Blocks, path: '/user/blueprints' },
  ]

  const adminItem: MenuItem = {
    label: '管理仪表板',
    icon: Shield,
    path: '/admin',
    adminOnly: true,
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2 rounded-full px-1 py-1 transition-all duration-200',
            'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0'
          )}
        >
          <div className="relative">
            <div className="overflow-hidden rounded-md">
              <img
                src={resolveMediaUrl(user?.avatar)}
                alt={user?.username || 'Steve'}
                className="h-8 w-8 object-contain"
                style={{ imageRendering: 'pixelated' }}
              />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500 dark:border-[#181818]" />
          </div>
          <span className="hidden text-sm font-medium text-slate-900 dark:text-slate-100 md:block">
            {user?.username}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[220px] rounded-[1.4rem] border-[#dfe3ef]/80 bg-[#f8f8fc]/92 p-2 shadow-[0_20px_50px_rgba(15,23,42,0.14)] backdrop-blur-xl dark:border-neutral-800/80 dark:bg-[#1a1b1d]/92">
        {/* 普通用户菜单项 */}
        {menuItems.map((item) => (
          <DropdownMenuItem
            key={item.label}
            onClick={() => item.path && navigate(item.path)}
            className="h-9 cursor-pointer px-3 py-2"
          >
            <item.icon className="mr-2 h-4 w-4" />
            {item.label}
          </DropdownMenuItem>
        ))}

        {/* 管理员菜单项 */}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                if (adminItem.path) {
                  navigate(adminItem.path)
                }
              }}
              className="h-9 cursor-pointer px-3 py-2 text-indigo-600 dark:text-indigo-400"
            >
              <adminItem.icon className="mr-2 h-4 w-4" />
              {adminItem.label}
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        {/* 退出账户 */}
        <DropdownMenuItem
          onClick={handleLogout}
          className="h-9 cursor-pointer px-3 py-2 text-red-600"
        >
          <LogOut className="mr-2 h-4 w-4" />
          退出账户
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
